import { useEffect, useRef, useState } from 'react';
import { Collapsible, PageHeader, Panel, Toolbar } from '../components/Page';
import { EmptyState, ErrorState, InlineError, TableSkeleton } from '../components/Feedback';
import { Chip } from '../components/Chips';
import { TextField } from '../components/Form';
import { TableWrap } from '../components/Table';
import { SortableTh } from '../components/SortableTh';
import { Pagination } from '../components/Pagination';
import { Drawer } from '../components/Overlay';
import { VendorTeachingPanel } from '../components/VendorTeachingPanel';
import { VendorLearningPanel } from '../components/VendorLearningPanel';
import { VendorTradingPartnerPanel } from '../components/VendorTradingPartnerPanel';
import { VendorPermittedSendersPanel } from '../components/VendorPermittedSendersPanel';
import { useAuth } from '../lib/auth';
import { vendors } from '../lib/endpoints';
import { useAsync, useDebounced } from '../lib/hooks';
import { useSortableTable } from '../lib/useSortableTable';
import { useToast } from '../lib/toast';
import { errorMessage } from '../lib/api';
import { formatDateTime, formatMoney, formatRelative } from '../lib/format';
import type { UpsertVendorRequest, VendorDto } from '../lib/types';

type VendorColumn = 'name' | 'contact' | 'taxId' | 'balance' | 'status';

function vendorSortValue(vendor: VendorDto, key: VendorColumn): string | number {
  switch (key) {
    case 'name':
      return vendor.displayName;
    case 'contact':
      return vendor.email ?? '';
    case 'taxId':
      return vendor.taxIdentifier ?? '';
    case 'balance':
      return vendor.balance ?? 0;
    case 'status':
      return vendor.active ? 1 : 0;
    default:
      return '';
  }
}

const EMPTY_VENDOR: UpsertVendorRequest = {
  qboVendorId: null,
  displayName: '',
  companyName: null,
  email: null,
  phone: null,
  taxIdentifier: null,
  addressLine1: null,
  addressLine2: null,
  city: null,
  state: null,
  postalCode: null,
  country: null,
  termsId: null,
  currencyCode: null,
};

function toForm(vendor: VendorDto): UpsertVendorRequest {
  return {
    qboVendorId: vendor.qboVendorId,
    displayName: vendor.displayName,
    companyName: vendor.companyName,
    email: vendor.email,
    phone: vendor.phone,
    taxIdentifier: vendor.taxIdentifier,
    addressLine1: vendor.addressLine1,
    addressLine2: null,
    city: vendor.city,
    state: vendor.state,
    postalCode: vendor.postalCode,
    country: vendor.country,
    termsId: null,
    currencyCode: vendor.currencyCode,
  };
}

/** A vendor is "stale" when the cache has not been refreshed from QuickBooks for a day. */
function syncTone(lastSyncedUtc: string): 'good' | 'warn' {
  const age = Date.now() - new Date(lastSyncedUtc).getTime();
  return age > 24 * 60 * 60 * 1000 ? 'warn' : 'good';
}

export function VendorsPage(): JSX.Element {
  const { can } = useAuth();
  const toast = useToast();
  const canManage = can('vendors.manage');

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const debouncedSearch = useDebounced(search, 350);

  const [editing, setEditing] = useState<VendorDto | 'new' | null>(null);

  // Whether the card opens with its Invoice template section already unfolded. "Learn from invoice"
  // sets the intent BEFORE the card opens; a ref rather than state because the effect below runs
  // after `editing` changes and would otherwise fold the section straight back up.
  const [teaching, setTeaching] = useState(false);
  const openTeaching = useRef(false);

  // Same pattern as `teaching`, for the AI Learning section: "AI Learning" in the row actions opens
  // the card with this section unfolded instead of the invoice-template one.
  const [learning, setLearning] = useState(false);
  const openLearning = useRef(false);

  // Trading Partner starts collapsed every time the card opens - unlike Learn from invoice/AI
  // Learning, nothing on the list row jumps straight to it, so there is no saved intent to restore.
  const [tradingPartner, setTradingPartner] = useState(false);

  // Same story as Trading Partner: no saved intent to restore, so this always starts collapsed.
  const [permittedSenders, setPermittedSenders] = useState(false);

  const [form, setForm] = useState<UpsertVendorRequest>(EMPTY_VENDOR);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [pulling, setPulling] = useState(false);

  // Consumed once by the "new vendor" branch of the effect below, then cleared, so it never
  // survives to affect an unrelated later "New vendor" click.
  const [duplicateSource, setDuplicateSource] = useState<VendorDto | null>(null);

  const list = useAsync(
    (signal) => vendors.list({ search: debouncedSearch, page, pageSize }, signal),
    [debouncedSearch, page, pageSize],
  );

  const table = useSortableTable<VendorColumn>({
    storageKey: 'vendorsListColumnWidths',
    columns: {
      name: { minWidth: 160 },
      contact: { minWidth: 140 },
      taxId: { minWidth: 100 },
      balance: { minWidth: 100 },
      status: { minWidth: 140 },
    },
  });

  useEffect(() => {
    if (editing === 'new') {
      // Same details as the source vendor, minus the two fields that make this a genuinely
      // different QuickBooks record: no id (it doesn't exist there yet) and no currency
      // (QuickBooks locks a vendor's currency permanently at creation, so this is exactly the
      // field a "different currency" duplicate exists to change).
      setForm(duplicateSource ? { ...toForm(duplicateSource), qboVendorId: null, currencyCode: null } : EMPTY_VENDOR);
      setDuplicateSource(null);
    } else if (editing) setForm(toForm(editing));
    setSaveError(null);
    setTeaching(openTeaching.current);
    openTeaching.current = false;
    setLearning(openLearning.current);
    openLearning.current = false;
    setTradingPartner(false);
    setPermittedSenders(false);
  }, [editing]);

  const setField = (key: keyof UpsertVendorRequest, value: string) => {
    setForm((current) => ({ ...current, [key]: value === '' ? null : value }));
  };

  const save = async () => {
    if (form.displayName.trim() === '') {
      setSaveError('A display name is required.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      if (editing !== 'new' && editing) {
        await vendors.update(editing.qboVendorId, form);
        toast.success('Vendor updated', `${form.displayName} was written back to QuickBooks.`);
      } else {
        await vendors.create(form);
        toast.success('Vendor created', `${form.displayName} was created in QuickBooks.`);
      }
      setEditing(null);
      list.reload();
    } catch (error) {
      const message = errorMessage(error);
      setSaveError(message);
      toast.error('Save failed', message);
    } finally {
      setSaving(false);
    }
  };

  const pull = async () => {
    setPulling(true);
    try {
      const response = await vendors.pull();
      toast.success(
        'Vendors pulled',
        `${response.recordsPulled} read · ${response.recordsCreated} new · ${response.recordsUpdated} updated.`,
      );
      list.reload();
    } catch (error) {
      toast.error('Pull failed', errorMessage(error));
    } finally {
      setPulling(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Vendors"
        description="The gateway's cache of QuickBooks vendors, used for matching extracted invoices."
        actions={
          <>
            {canManage ? (
              <button type="button" className="btn btn-secondary" onClick={pull} disabled={pulling}>
                {pulling ? 'Pulling…' : 'Pull from QuickBooks'}
              </button>
            ) : null}
            {canManage ? (
              <button type="button" className="btn btn-secondary" onClick={() => setEditing('new')}>
                New vendor
              </button>
            ) : null}
          </>
        }
      />

      <Panel>
        <Toolbar>
          <TextField
            label="Search"
            type="search"
            value={search}
            onChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
            placeholder="Name, company, email or tax id"
            className="toolbar-grow"
          />
        </Toolbar>

        {list.loading && !list.data ? (
          <TableSkeleton rows={10} columns={6} />
        ) : list.error ? (
          <ErrorState message="Vendors could not be loaded." detail={list.error} onRetry={list.reload} />
        ) : !list.data || list.data.items.length === 0 ? (
          <EmptyState
            title="No vendors cached"
            description="Pull from QuickBooks to populate the local vendor cache."
            action={
              canManage ? (
                <button type="button" className="btn btn-primary" onClick={pull} disabled={pulling}>
                  Pull from QuickBooks
                </button>
              ) : undefined
            }
          />
        ) : (
          <>
            <TableWrap busy={list.loading} tableClassName="data-table-sortable">
              <colgroup>
                <col style={table.columnStyle('name')} />
                <col style={table.columnStyle('contact')} />
                <col style={table.columnStyle('taxId')} />
                <col style={table.columnStyle('balance')} />
                <col style={table.columnStyle('status')} />
                <col className="col-actions col-actions-wide" />
              </colgroup>
              <thead>
                <tr>
                  <SortableTh
                    label="Vendor"
                    sortKey="name"
                    currentSortKey={table.sortKey}
                    sortDir={table.sortDir}
                    onSort={table.toggleSort}
                    style={table.columnStyle('name')}
                    onResizeStart={table.beginResize('name')}
                  />
                  <SortableTh
                    label="Contact"
                    sortKey="contact"
                    currentSortKey={table.sortKey}
                    sortDir={table.sortDir}
                    onSort={table.toggleSort}
                    style={table.columnStyle('contact')}
                    onResizeStart={table.beginResize('contact')}
                  />
                  <SortableTh
                    label="Tax id"
                    sortKey="taxId"
                    currentSortKey={table.sortKey}
                    sortDir={table.sortDir}
                    onSort={table.toggleSort}
                    style={table.columnStyle('taxId')}
                    onResizeStart={table.beginResize('taxId')}
                  />
                  <SortableTh
                    label="Balance"
                    sortKey="balance"
                    currentSortKey={table.sortKey}
                    sortDir={table.sortDir}
                    onSort={table.toggleSort}
                    className="num"
                    style={table.columnStyle('balance')}
                    onResizeStart={table.beginResize('balance')}
                  />
                  <SortableTh
                    label="Sync status"
                    sortKey="status"
                    currentSortKey={table.sortKey}
                    sortDir={table.sortDir}
                    onSort={table.toggleSort}
                    style={table.columnStyle('status')}
                    onResizeStart={table.beginResize('status')}
                  />
                  <th scope="col" className="col-actions col-actions-wide">
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {table.sort(list.data.items, vendorSortValue).map((vendor) => (
                  <tr key={vendor.id}>
                    <td>
                      <span className="cell-strong">{vendor.displayName}</span>
                      <span className="cell-sub">
                        {vendor.companyName ?? '—'} · <span className="mono">{vendor.qboVendorId}</span>
                      </span>
                    </td>
                    <td>
                      {vendor.email ?? '—'}
                      <span className="cell-sub">{vendor.phone ?? ''}</span>
                    </td>
                    <td className="mono">{vendor.taxIdentifier ?? '—'}</td>
                    <td className="num">{formatMoney(vendor.balance, vendor.currencyCode)}</td>
                    <td>
                      <Chip tone={vendor.active ? syncTone(vendor.lastSyncedUtc) : 'neutral'}>
                        {vendor.active ? 'Active' : 'Inactive'}
                      </Chip>
                      <span className="cell-sub">synced {formatRelative(vendor.lastSyncedUtc)}</span>
                    </td>
                    <td className="col-actions col-actions-wide">
                      {canManage ? (
                        // The single entry point into this vendor's card. "Learn from invoice" and
                        // "AI Learning" used to be separate row buttons that jumped straight to a
                        // section inside it; they're reached from inside the open card now
                        // (Invoice template / AI Learning below the vendor's own fields) so the list
                        // row carries one action instead of four.
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing(vendor)}>
                          Edit
                        </button>
                      ) : null}
                      {canManage ? (
                        // QuickBooks locks a vendor's currency permanently at creation, so a
                        // partner who starts invoicing in a second currency needs a second vendor
                        // card there. This copies everything else so only the currency (and the
                        // name, if it needs to say so) has to be typed in.
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => {
                            setDuplicateSource(vendor);
                            setEditing('new');
                          }}
                        >
                          Duplicate for another currency
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>

            <Pagination
              page={list.data.page}
              pageSize={list.data.pageSize}
              totalCount={list.data.totalCount}
              totalPages={list.data.totalPages}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          </>
        )}
      </Panel>

      <Drawer
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing === 'new' ? 'New vendor' : 'Edit vendor'}
        description={
          editing === 'new'
            ? form.displayName
              ? 'Duplicated from an existing vendor. Change the currency (and the name, if it needs to say so) before saving — everything else was copied.'
              : 'Creates the vendor in QuickBooks Online and caches it locally.'
            : 'Writes changes back to QuickBooks Online.'
        }
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setEditing(null)} disabled={saving}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save vendor'}
            </button>
          </>
        }
      >
        <InlineError message={saveError} />
        <div className="form-grid">
          <TextField
            label="Display name"
            value={form.displayName}
            onChange={(value) => setForm((current) => ({ ...current, displayName: value }))}
            required
            className="form-span-2"
          />
          <TextField label="Company name" value={form.companyName ?? ''} onChange={(v) => setField('companyName', v)} />
          <TextField label="Tax identifier" value={form.taxIdentifier ?? ''} onChange={(v) => setField('taxIdentifier', v)} />
          <TextField label="Email" type="email" value={form.email ?? ''} onChange={(v) => setField('email', v)} />
          <TextField label="Phone" value={form.phone ?? ''} onChange={(v) => setField('phone', v)} />
          <TextField label="Address line 1" value={form.addressLine1 ?? ''} onChange={(v) => setField('addressLine1', v)} />
          <TextField label="Address line 2" value={form.addressLine2 ?? ''} onChange={(v) => setField('addressLine2', v)} />
          <TextField label="City" value={form.city ?? ''} onChange={(v) => setField('city', v)} />
          <TextField label="State / county" value={form.state ?? ''} onChange={(v) => setField('state', v)} />
          <TextField label="Postal code" value={form.postalCode ?? ''} onChange={(v) => setField('postalCode', v)} />
          <TextField label="Country" value={form.country ?? ''} onChange={(v) => setField('country', v)} />
          <TextField
            label="Currency code"
            value={form.currencyCode ?? ''}
            onChange={(v) => setField('currencyCode', v.toUpperCase())}
            placeholder="USD"
            hint={
              editing === 'new' && form.displayName
                ? 'Cannot be changed once this vendor is created in QuickBooks — this is the one field a duplicate exists to set.'
                : undefined
            }
          />
          <TextField
            label="Terms id"
            value={form.termsId ?? ''}
            onChange={(v) => setField('termsId', v)}
            hint="QuickBooks Term reference id."
          />
        </div>

        {editing !== 'new' && editing ? (
          <p className="drawer-note">
            Last synced {formatDateTime(editing.lastSyncedUtc)} · QuickBooks id{' '}
            <span className="mono">{editing.qboVendorId}</span>
          </p>
        ) : null}
        {canManage ? (
          /*
            THE TEACHING HALF OF THE CARD. The fields above are who the supplier IS; this is what
            their invoices LOOK LIKE. Both belong to the same record, so both belong on the same
            card - which is the whole point of moving it here from a separate page.

            Collapsed by default so the details form stays the first thing seen, and bound to this
            vendor's QuickBooks id so an intake started here can only ever update this supplier.
            While a vendor is being created there is no id yet, and the document is what creates it.
          */
          <Collapsible
            title="Invoice template"
            hint={
              editing === 'new'
                ? 'Create this supplier from one of their invoices, and teach the layout at the same time.'
                : 'Teach iERP AI what this supplier\u2019s invoices look like.'
            }
            open={teaching}
            onOpenChange={setTeaching}
          >
            <VendorTeachingPanel
              qboVendorId={editing !== 'new' && editing ? editing.qboVendorId : null}
              vendorName={editing !== 'new' && editing ? editing.displayName : null}
              onCompleted={() => {
                // An approved intake writes the supplier in QuickBooks, so the cached row this card
                // was opened from is now stale either way.
                setEditing(null);
                list.reload();
              }}
            />
          </Collapsible>
        ) : null}

        {canManage && editing !== 'new' && editing ? (
          /*
            AI LEARNING, ON THE SAME CARD. Not a separate global screen: this vendor is already
            open, and the teaching panel just above is what makes learning possible in the first
            place, so both belong together. There is no id to bind to for a vendor still being
            created, so this section only appears once the supplier actually exists.
          */
          <Collapsible
            title="AI Learning"
            hint="Turn this supplier's learning on, and send its history to iERP AI once its layout is taught."
            open={learning}
            onOpenChange={setLearning}
          >
            <VendorLearningPanel qboVendorId={editing.qboVendorId} vendorName={editing.displayName} />
          </Collapsible>
        ) : null}

        {canManage && editing !== 'new' && editing ? (
          <Collapsible
            title="Trading Partner"
            hint="Connect this supplier as an iERP AI trading partner for direct document exchange, bypassing email."
            open={tradingPartner}
            onOpenChange={setTradingPartner}
          >
            <VendorTradingPartnerPanel qboVendorId={editing.qboVendorId} vendorName={editing.displayName} />
          </Collapsible>
        ) : null}

        {canManage && editing !== 'new' && editing ? (
          <Collapsible
            title="Permitted senders"
            hint="Which email domains or addresses this vendor's invoices are trusted to arrive from."
            open={permittedSenders}
            onOpenChange={setPermittedSenders}
          >
            <VendorPermittedSendersPanel qboVendorId={editing.qboVendorId} vendorName={editing.displayName} />
          </Collapsible>
        ) : null}
      </Drawer>
    </>
  );
}
